import { Action } from 'redux';
import {
  ActionTypes,
  actionTypes,
  apiStatuses,
  exceptionTypes,
  IExceptionAction,
} from './actions/Actions';
import {
  IAddress,
  IApiState,
  ICreateOrder,
  IOrganizationSearch,
  IPayex,
  IProductInfo,
  IReferer,
  ISessionInfo,
  IUserInfo,
  IUtm,
} from './CustomTypes';
import { setLoyaltyCode } from './fastflowUtils';

export interface IApiAction extends Action {
  status: apiStatuses;
}
export interface ISimpleReducerAction extends IApiAction {
  data: any;
}
export type SimpleReducer<T = unknown & IApiState> = (
  state: T,
  action: ISimpleReducerAction,
) => T;

export function simpleReducer<T = unknown & IApiState>(
  actionType: ActionTypes,
  initialState?: T,
): (state: T, action: ISimpleReducerAction) => T {
  return (
    state = Object.assign({}, initialState, { apiStatus: null }),
    action: ISimpleReducerAction,
  ) => {
    switch (action.type) {
      case actionType:
        return Object.assign(
          {},
          state,
          typeof action.data === 'object' ? action.data : { data: action.data },
          { apiStatus: action.status },
        );
      default:
        return state;
    }
  };
}
export const productInfo = simpleReducer<IProductInfo>(
  actionTypes.SET_PRODUCT,
  {
    allowedPaymentMethods: [],
    owningClient: '',
    rckEnableFirstservice: false,
    rckEnableOnsite: false,
  },
);
export const productQuarantined = simpleReducer<IProductInfo>(
  actionTypes.SET_PRODUCT_QUARANTINED,
);
export const alternateOfferProductInfo = simpleReducer<IProductInfo>(
  actionTypes.SET_ALTERNATE_OFFER_PRODUCT,
  { allowedPaymentMethods: [], owningClient: '' },
);
export const fullPriceProductInfo = simpleReducer<IProductInfo>(
  actionTypes.SET_FULL_PRICE_PRODUCT,
  { allowedPaymentMethods: [], owningClient: '' },
);
export const upsaleProductInfo = simpleReducer<IProductInfo>(
  actionTypes.SET_UPSALE_PRODUCT_INFO,
  { allowedPaymentMethods: [], owningClient: '' },
);
export const createdOrder = simpleReducer<ICreateOrder>(
  actionTypes.CREATE_ORDER,
  { loadingText: 'Registrerer bestilling...' },
);
export const sessionInfo = simpleReducer<ISessionInfo>(
  actionTypes.SET_SESSION_INFO,
);
export const findUserByPhoneNumber = simpleReducer(
  actionTypes.FIND_USER_BY_PHONE_NUMBER,
);
export const customerServiceInfo = simpleReducer(
  actionTypes.FETCH_CUSTOMERSERVICE_INFO,
);
export const fetchUserInfo = simpleReducer<IUserInfo>(
  actionTypes.FETCH_USER_INFO,
);
export const sendSms = simpleReducer(actionTypes.SEND_SMS);
export const sendLoginLink = simpleReducer(actionTypes.SEND_LOGIN_LINK);
export const checkCode = simpleReducer(actionTypes.CHECK_CODE);
export const checkDuplicateAndQuarantine = simpleReducer(
  actionTypes.CHECK_DUPLICATE_AND_QUARANTINE,
);
export const findUserByEmail = simpleReducer(actionTypes.FIND_USER_BY_EMAIL);
export const createSpidUser = simpleReducer(actionTypes.CREATE_SPID_USER);
export const addFamilySharing = simpleReducer(actionTypes.ADD_FAMILY_SHARING);
export const cancelPayment = simpleReducer(actionTypes.CANCEL_PAYMENT);

export const userInfo = (
  state = {
    birthDay: '',
    birthMonth: '',
    birthYear: '',
    customerNumber: null,
    email: '',
    firstName: '',
    lastName: '',
    loyaltyChecked: false,
    loyaltyCode: '',
    paymentMethods: [],
    phoneNumber: '',
    sig: null,
    sigFromCode: false,
    spidId: null,
    spidPrimaryEmail: '',
    valid: false,
    validUntil: null,
    vippsEnabled: false,
  } as IUserInfo,
  action: ISimpleReducerAction | any,
) => {
  switch (action.type) {
    case actionTypes.SET_SESSION_INFO: {
      if (action.status !== apiStatuses.SUCCESS) {
        return state;
      }
      const validUntil = Date.now() + action.data.expiresIn * 1000;
      return {
        ...state,
        sig: action.data.sig,
        spidId: action.data.userId,
        validUntil,
      };
    }
    case actionTypes.CHECK_CODE: {
      if (action.status !== apiStatuses.SUCCESS) {
        return state;
      }
      const validUntil = Date.now() + action.data.expiresIn * 1000;
      return {
        ...state,
        firstName: action.data.firstName,
        lastName: action.data.lastName,
        paymentMethods: action.data.paymentDetails || [],
        phoneNumber: action.data.cellphone,
        sig: action.data.sig,
        sigFromCode: true,
        spidId: action.data.spidId,
        spidPrimaryEmail: action.data.spidPrimaryEmail,
        validUntil,
      };
    }
    case actionTypes.CLEAR_SPID_TOKEN: {
      return {
        ...state,
        sig: null,
      };
    }
    case actionTypes.FETCH_USER_INFO: {
      if (action.status !== apiStatuses.SUCCESS) {
        return state;
      }
      return {
        ...state,
        acceptedAgreement: action.data.acceptedAgreement,
        birthDay: action.data.birthDay || state.birthDay,
        email: action.data.email,
        firstName: action.data.firstName,
        lastName: action.data.lastName,
        paymentMethods: action.data.paymentDetails || [],
        phoneNumber: action.data.cellphone,
        spidPrimaryEmail: action.data.spidPrimaryEmail,
        vippsEnabled: action.data.vippsEnabled,
      };
    }
    case actionTypes.FIND_USER_BY_EMAIL: {
      if (action.status !== apiStatuses.SUCCESS) {
        return state;
      }
      return { ...state, customerNumber: action.data.customernumber };
    }
    case actionTypes.FIND_USER_BY_PHONE_NUMBER: {
      if (action.status !== apiStatuses.SUCCESS) {
        return state;
      }
      return { ...state, customerNumber: action.data.customernumber };
    }
    case actionTypes.SET_SIG_AND_SPIDID_FROM_URL: {
      return { ...state, spidId: action.spidId, sig: action.sig };
    }
    case actionTypes.USER_ENTERS_EMAIL:
      return { ...state, email: action.email };
    case actionTypes.USER_ENTERS_FIRST_NAME:
      return { ...state, firstName: action.firstName };
    case actionTypes.USER_ENTERS_LAST_NAME:
      return { ...state, lastName: action.lastName };
    case actionTypes.USER_ENTERS_PHONE_NUMBER:
      return { ...state, phoneNumber: action.number };
    case actionTypes.USER_ENTERS_BIRTH_DAY:
      return { ...state, birthDay: action.birthDay };
    case actionTypes.USER_ENTERS_BIRTH_MONTH:
      return { ...state, birthMonth: action.birthMonth };
    case actionTypes.USER_ENTERS_BIRTH_YEAR:
      return { ...state, birthYear: action.birthYear };
    case actionTypes.USER_ENTERS_LOYALTY_CODE:
      return { ...state, loyaltyCode: action.loyaltyCode, valid: false };
    case actionTypes.VALIDATE_LOYALTY_CODE:
      if (action.status !== apiStatuses.SUCCESS) {
        return state;
      }
      if (state.loyaltyCode !== undefined) {
        setLoyaltyCode(state.loyaltyCode);
      }
      return { ...state, valid: action.data.valid, loyaltyChecked: true };
    default:
      return state;
  }
};

export const address = (
  state = {
    apartmentNumber: '',
    city: '',
    coAddress: '',
    deliveryPointId: '',
    entrance: '',
    floor: '',
    houseNumber: '',
    streetAddress: '',
    zipCode: '',
  } as IAddress,
  action: ISimpleReducerAction | any,
) => {
  switch (action.type) {
    case actionTypes.CHECK_CODE: {
      if (action.status !== apiStatuses.SUCCESS) {
        return state;
      }
      return {
        ...state,
        apartmentNumber: action.data.apartmentNumber,
        city: action.data.city,
        deliveryPointId: action.data.diAddressCode,
        entrance: action.data.entrance,
        floor: action.data.floor,
        houseNumber: action.data.houseNumber,
        streetAddress: action.data.streetAddress,
        zipCode: action.data.zipCode,
      };
    }
    case actionTypes.FETCH_USER_INFO: {
      if (action.status !== apiStatuses.SUCCESS) {
        return state;
      }
      return {
        ...state,
        apartmentNumber: action.data.apartmentNumber,
        city: action.data.city,
        deliveryPointId: action.data.diAddressCode,
        entrance: action.data.entrance,
        floor: action.data.floor,
        houseNumber: action.data.houseNumber,
        streetAddress: action.data.streetAddress,
        zipCode: action.data.zipCode,
      };
    }
    case actionTypes.USER_ENTERS_CITY:
      return { ...state, city: action.city };
    case actionTypes.USER_ENTERS_ZIPCODE:
      return { ...state, zipCode: action.zipCode };
    case actionTypes.USER_ENTERS_STREET_ADDRESS:
      return { ...state, streetAddress: action.streetAddress };
    case actionTypes.USER_ENTERS_HOUSE_NUMBER:
      return { ...state, houseNumber: action.houseNumber };
    case actionTypes.USER_ENTERS_ENTRANCE:
      return { ...state, entrance: action.entrance };
    case actionTypes.USER_ENTERS_FLOOR:
      return { ...state, floor: action.floor };
    case actionTypes.USER_ENTERS_APARTMENT_NUMBER:
      return { ...state, apartmentNumber: action.apartmentNumber };
    case actionTypes.USER_ENTERS_DELIVERY_POINT_ID:
      return { ...state, deliveryPointId: action.deliveryPointId };
    case actionTypes.USER_ENTERS_CO_ADDRESS:
      return { ...state, coAddress: action.coAddress };

    default:
      return state;
  }
};

export const code = (
  state = { code: '', sent: false, sentCodeBySms: true },
  action: ISimpleReducerAction | any,
) => {
  switch (action.type) {
    case actionTypes.USER_ENTERS_CODE:
      return { ...state, code: action.code };
    case actionTypes.FIND_USER_BY_EMAIL: {
      if (action.status !== apiStatuses.SUCCESS) {
        return state;
      }
      return { ...state, sent: true, sentCodeBySms: action.data.sentCodeBySms };
    }
    default:
      return state;
  }
};

export const timeout = (state = { showWarning: false }, action: Action) => {
  switch (action.type) {
    case actionTypes.SHOW_TIMEOUT:
      return { ...state, showWarning: true };
    case actionTypes.HIDE_TIMEOUT:
      return { ...state, showWarning: false };
    default:
      return state;
  }
};

export const referer = (
  state = { referer: '', redirectUri: '' } as IReferer,
  action: Action | any,
) => {
  switch (action.type) {
    case actionTypes.SET_REFERER:
      return { ...state, referer: action.referer };
    case actionTypes.SET_REDIRECT_URI:
      return { ...state, redirectUri: action.redirectUri };
    default:
      return state;
  }
};

export const payex = (
  state = {
    loading: false,
    widgetUrl: '',
  } as IPayex,
  action: Action | any,
) => {
  if (action.type === actionTypes.SET_PAYEX_URL) {
    if (action.status === apiStatuses.SUCCESS) {
      return {
        ...state,
        loading: false,
        widgetUrl: action.data.widgetUrl,
      };
    } else {
      return {
        ...state,
        loading: true,
      };
    }
  } else {
    return state;
  }
};

export const utm = (
  state = {
    campaign: '',
    content: '',
    medium: '',
    source: '',
    term: '',
  } as IUtm,
  action: Action | any,
) => {
  switch (action.type) {
    case actionTypes.SET_UTM_SOURCE:
      return { ...state, source: action.source };
    case actionTypes.SET_UTM_MEDIUM:
      return { ...state, medium: action.medium };
    case actionTypes.SET_UTM_CAMPAIGN:
      return { ...state, campaign: action.campaign };
    case actionTypes.SET_UTM_TERM:
      return { ...state, term: action.term };
    case actionTypes.SET_UTM_CONTENT:
      return { ...state, content: action.content };
    default:
      return state;
  }
};

export const organizationData = (
  state = {
    input: '',
  } as IOrganizationSearch,
  action: Action | any,
) => {
  switch (action.type) {
    case actionTypes.SET_BRREG_INFO:
      const data = action.data;
      return { ...state, data };
    case actionTypes.SET_BRREG_INPUT:
      return { ...state, input: action.orgnumber };
    default:
      return state;
  }
};

export const apiErrors = (state: Action[] = [], action: IApiAction) => {
  if (
    action.status &&
    [apiStatuses.SUCCESS, apiStatuses.LOADING].indexOf(action.status) === -1
  ) {
    return state.concat(action);
  }
  return state;
};

export const exceptions = (
  state: IExceptionAction[] = [],
  action: IExceptionAction | any,
) => {
  switch (action.type) {
    case exceptionTypes.EXCEPTION:
      return [...state, action];
    default:
      return state;
  }
};
